import {App as AntdApp, Alert, Divider} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetGscAuthUrlMutation,
	useGetGscAccountsQuery,
	useUseGscAccountMutation,
} from "~/reduxs/api/gscApiSlice";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);

/**
 * Panel bắt đầu kết nối OAuth. Lấy URL consent từ BE, lưu `state` vào sessionStorage
 * (để callback đối chiếu chống CSRF nhẹ) rồi chuyển hướng cả trang sang Google.
 *
 * NGOÀI RA: nếu user ĐÃ kết nối tài khoản Google ở dự án khác, hiện danh sách để "dùng lại"
 * cho dự án này mà KHÔNG phải OAuth lại (credential dùng chung — `gsc_accounts`).
 */
function GscConnect({projectId, revoked = false, email}) {

	const {notification} = AntdApp.useApp();
	const [getAuthUrl, {isLoading}] = useGetGscAuthUrlMutation();
	const {data: accounts = []} = useGetGscAccountsQuery();
	const [applyAccount, {isLoading: using}] = useUseGscAccountMutation();

	// Chỉ gợi ý "dùng lại" các tài khoản còn hiệu lực (revoked phải kết nối lại bằng Google).
	const reusable = accounts.filter((a) => a.status !== 'revoked');

	const handleConnect = async () => {
		try {
			const res = await getAuthUrl(projectId).unwrap();
			if (!res?.url) throw new Error('Không lấy được URL kết nối');
			// Lưu state để callback verify; kèm projectId để callback route lại đúng dự án.
			sessionStorage.setItem('gsc_oauth_state', res.state || '');
			window.location.href = res.url;
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thể bắt đầu kết nối Google')});
		}
	};

	const handleUse = async (accountId) => {
		try {
			await applyAccount({projectId, account_id: accountId}).unwrap();
			notification.success({message: 'Đã dùng tài khoản', description: 'Hãy chọn property cho dự án này.'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không dùng được tài khoản này')});
		}
	};

	return (
		<div className={cn('connectPanel')}>
			<div className={cn('connectIcon')}>
				<FontAwesomeIcon icon="fa-brands fa-google" />
			</div>
			<div className={cn('connectTitle')}>
				{revoked ? 'Kết nối lại Google Search Console' : 'Kết nối Google Search Console'}
			</div>
			<div className={cn('connectDesc')}>
				Kết nối tài khoản Google sở hữu website để đồng bộ dữ liệu hiệu suất tìm kiếm thật
				(clicks, impressions, từ khoá, vị trí xếp hạng). Chúng tôi chỉ xin quyền <strong>chỉ đọc</strong>.
			</div>

			{revoked && (
				<Alert
					type="warning"
					showIcon
					style={{textAlign: 'left', marginBottom: 18}}
					message="Kết nối trước đã bị thu hồi hoặc hết hạn"
					description={email ? `Tài khoản: ${email}. Vui lòng cấp quyền lại để tiếp tục đồng bộ.` : 'Vui lòng cấp quyền lại để tiếp tục đồng bộ.'}
				/>
			)}

			{/* Dùng lại tài khoản Google đã kết nối (không cần OAuth lại) — chỉ khi chưa bị thu hồi. */}
			{!revoked && reusable.length > 0 && (
				<div style={{textAlign: 'left', marginBottom: 8}}>
					<div style={{fontSize: 13, color: '#6b7280', marginBottom: 8}}>
						Dùng lại tài khoản Google đã kết nối:
					</div>
					{reusable.map((a) => (
						<div key={a.id} style={{
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
							gap: 12, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8,
						}}>
							<span style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 0}}>
								<FontAwesomeIcon icon="fa-brands fa-google" />
								<span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{a.email || `Tài khoản #${a.id}`}</span>
							</span>
							<Button small primary loading={using} onClick={() => handleUse(a.id)}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-plug" />}>
								Dùng cho dự án này
							</Button>
						</div>
					))}
					<Divider plain style={{margin: '14px 0', color: '#9ca3af', fontSize: 12}}>hoặc</Divider>
				</div>
			)}

			<Button primary loading={isLoading} onClick={handleConnect}
				leftIcon={<FontAwesomeIcon icon="fa-brands fa-google" />}>
				{revoked ? 'Kết nối lại' : (reusable.length > 0 ? 'Kết nối tài khoản Google khác' : 'Kết nối tài khoản Google')}
			</Button>
		</div>
	);
}

export default GscConnect;
